import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CalendarIcon, ClockIcon, SearchIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { Logo } from '../components/Logo';
import { blogPosts, BlogPost } from '../data/blogPosts';
const POSTS_PER_PAGE = 6;
const estimateReadTime = (content: string) => {
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 220));
  return `${minutes} min read`;
};
export const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);
  const featuredPost = useMemo(() => blogPosts.find(post => post.featured) || blogPosts[0], []);
  const categories = useMemo(() => Array.from(new Set(blogPosts.map(post => post.category))), []);
  const filteredPosts = useMemo(() => {
    let posts = blogPosts.filter(post => post.id !== featuredPost.id);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(post => post.title.toLowerCase().includes(q) || post.excerpt.toLowerCase().includes(q) || post.author.toLowerCase().includes(q));
    }
    if (selectedCategory) {
      posts = posts.filter(post => post.category === selectedCategory);
    }
    return posts;
  }, [searchQuery, selectedCategory, featuredPost.id]);
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const currentPosts = filteredPosts.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);
  const isFiltering = Boolean(searchQuery) || selectedCategory !== null;
  return <div className="bg-[#f2efeb] text-[#2b2826] min-h-screen w-full" data-id="element-651">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 w-full z-50 py-6 px-4 sm:px-6 lg:px-8" data-id="element-652">
        <div className="max-w-7xl mx-auto flex justify-between items-center bg-[#2b2826] backdrop-blur-md py-3 px-6 rounded-full border border-[#2b2826] shadow-lg" data-id="element-653">
          <Link to="/" data-id="element-654">
            <Logo data-id="element-655" />
          </Link>

          <div className="hidden md:flex items-center gap-8" data-id="element-656">
            <Link to="/" className="text-[#f2efeb] font-medium hover:text-[#c9a875] transition-colors" data-id="element-657">
              Home
            </Link>
            <Link to="/#services" className="text-[#f2efeb] font-medium hover:text-[#c9a875] transition-colors" data-id="element-658">
              Services
            </Link>
            <Link to="/#process" className="text-[#f2efeb] font-medium hover:text-[#c9a875] transition-colors" data-id="element-659">
              How It Works
            </Link>
            <Link to="/blog" className="text-[#c9a875] font-medium transition-colors" data-id="element-660">
              Blog
            </Link>
            <Link to="/#funding-form" className="bg-[#c9a875] px-5 py-2.5 rounded-full text-white hover:bg-[#b08e5c] transition-all focus:outline-none focus:ring-2 focus:ring-[#c9a875]/50 text-sm" data-id="element-661">
              Get Funded
            </Link>
          </div>

          <div className="md:hidden" data-id="element-662">
            <Link to="/#funding-form" className="bg-[#c9a875] px-4 py-2 rounded-full text-white hover:bg-[#b08e5c] transition-all text-sm font-medium" data-id="element-663">
              Get Funded
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="pt-36 sm:pt-44 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-664">
        <div className="text-center mb-10 sm:mb-12" data-id="element-665">
          <p className="text-xs sm:text-sm tracking-widest uppercase text-[#c9a875] font-medium mb-4" data-id="element-666">
            Journal
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl leading-tight mb-5" data-id="element-667">
            Wholesaling <span className="italic text-[#c9a875]" data-id="element-668">insights</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#2b2826]/70 font-light max-w-2xl mx-auto" data-id="element-669">
            Strategy, financing tactics, and field-tested advice for real-estate
            wholesalers from Cameron and the team.
          </p>
        </div>

        <div className="max-w-xl mx-auto relative" data-id="element-670">
          <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2b2826]/40" data-id="element-671" />
          <input type="text" placeholder="Search articles..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white border border-[#d9d3cb] rounded-full pl-12 pr-5 py-3 text-sm focus:border-[#c9a875] focus:outline-none text-[#2b2826] placeholder:text-[#2b2826]/40" data-id="element-672" />
        </div>
      </div>

      {/* Featured Article */}
      {!isFiltering && featuredPost && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 sm:mb-24" data-id="element-673">
          <div className="flex items-end justify-between mb-8" data-id="element-674">
            <p className="text-xs tracking-widest uppercase text-[#c9a875] font-medium" data-id="element-675">
              Featured
            </p>
            <span className="text-xs text-[#2b2826]/50" data-id="element-676">
              {featuredPost.date}
            </span>
          </div>

          <Link to={`/blog/${featuredPost.id}`} className="group grid md:grid-cols-12 gap-8 lg:gap-12 items-center" data-id="element-677">
            <div className="md:col-span-7 relative overflow-hidden rounded-2xl aspect-[16/10]" data-id="element-678">
              <img src={featuredPost.imageUrl} alt={featuredPost.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" data-id="element-679" />
              <div className="absolute top-4 left-4" data-id="element-680">
                <span className="bg-[#f2efeb]/95 backdrop-blur-sm text-[#2b2826] text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full" data-id="element-681">
                  {featuredPost.category}
                </span>
              </div>
            </div>

            <div className="md:col-span-5" data-id="element-682">
              <div className="flex items-center gap-4 text-xs text-[#2b2826]/60 mb-4" data-id="element-683">
                <span className="flex items-center gap-1.5" data-id="element-684">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-685" />
                  {featuredPost.date}
                </span>
                <span className="w-1 h-1 rounded-full bg-[#2b2826]/30" data-id="element-686" />
                <span className="flex items-center gap-1.5" data-id="element-687">
                  <ClockIcon className="w-3.5 h-3.5" data-id="element-688" />
                  {estimateReadTime(featuredPost.content)}
                </span>
              </div>

              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2b2826] leading-tight mb-5 group-hover:text-[#c9a875] transition-colors" data-id="element-689">
                {featuredPost.title}
              </h2>

              <p className="text-base sm:text-lg text-[#2b2826]/70 leading-relaxed font-light mb-6" data-id="element-690">
                {featuredPost.excerpt}
              </p>

              <div className="flex items-center justify-between pt-6 border-t border-[#2b2826]/15" data-id="element-691">
                <div className="flex items-center gap-3" data-id="element-692">
                  <div className="w-9 h-9 rounded-full bg-[#2b2826] flex items-center justify-center text-[#c9a875] font-serif-display text-base" data-id="element-693">
                    {featuredPost.author.charAt(0)}
                  </div>
                  <span className="text-sm text-[#2b2826]/80 font-medium" data-id="element-694">
                    {featuredPost.author}
                  </span>
                </div>
                <span className="inline-flex items-center gap-2 text-[#c9a875] font-medium text-sm group-hover:gap-3 transition-all" data-id="element-695">
                  Read article
                  <ArrowRightIcon className="w-4 h-4" data-id="element-696" />
                </span>
              </div>
            </div>
          </Link>
        </div>}

      {/* Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 sm:mb-12" data-id="element-697">
        <div className="flex items-center justify-between mb-6 sm:mb-8" data-id="element-698">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2b2826]" data-id="element-699">
            All articles
          </h2>
          <span className="text-xs text-[#2b2826]/50" data-id="element-700">
            {filteredPosts.length}{' '}
            {filteredPosts.length === 1 ? 'article' : 'articles'}
          </span>
        </div>
        <div className="flex overflow-x-auto pb-2 scrollbar-hide gap-2 sm:gap-3 border-b border-[#2b2826]/15" data-id="element-701">
          <button onClick={() => setSelectedCategory(null)} className={`whitespace-nowrap text-sm font-medium px-4 py-2.5 transition-colors !rounded-none !shadow-none hover:!shadow-none hover:!translate-y-0 !min-h-0 border-b-2 ${selectedCategory === null ? 'border-[#c9a875] text-[#c9a875]' : 'border-transparent text-[#2b2826]/60 hover:text-[#2b2826]'}`} data-id="element-702">
            All
          </button>
          {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`whitespace-nowrap text-sm font-medium px-4 py-2.5 transition-colors !rounded-none !shadow-none hover:!shadow-none hover:!translate-y-0 !min-h-0 border-b-2 ${selectedCategory === category ? 'border-[#c9a875] text-[#c9a875]' : 'border-transparent text-[#2b2826]/60 hover:text-[#2b2826]'}`} data-id="element-703">
              {category}
            </button>)}
        </div>
      </div>

      {/* Article Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28" data-id="element-704">
        {currentPosts.length === 0 ? <div className="text-center py-20" data-id="element-705">
            <div className="w-16 h-16 bg-[#2b2826]/5 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-706">
              <SearchIcon className="w-7 h-7 text-[#2b2826]/40" data-id="element-707" />
            </div>
            <h3 className="font-serif-display text-2xl text-[#2b2826] mb-2" data-id="element-708">
              No articles found
            </h3>
            <p className="text-[#2b2826]/60 text-sm mb-6" data-id="element-709">
              Try adjusting your search or category filter.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedCategory(null);
        }} className="text-[#c9a875] font-medium hover:underline !rounded-none !shadow-none hover:!shadow-none hover:!translate-y-0 !min-h-0" data-id="element-710">
              Clear filters
            </button>
          </div> : <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 sm:gap-y-16" data-id="element-711">
              {currentPosts.map(post => <Link key={post.id} to={`/blog/${post.id}`} className="group block" data-id="element-712">
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-5" data-id="element-713">
                    <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-id="element-714" />
                    <div className="absolute top-3 left-3" data-id="element-715">
                      <span className="bg-[#f2efeb]/95 backdrop-blur-sm text-[#2b2826] text-[10px] font-medium tracking-wider uppercase px-2.5 py-1 rounded-full" data-id="element-716">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-[#2b2826]/55 mb-3" data-id="element-717">
                    <span data-id="element-718">{post.date}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-[#2b2826]/30" data-id="element-719" />
                    <span data-id="element-720">{estimateReadTime(post.content)}</span>
                  </div>

                  <h3 className="font-serif-display text-xl sm:text-2xl text-[#2b2826] leading-snug mb-3 group-hover:text-[#c9a875] transition-colors line-clamp-2" data-id="element-721">
                    {post.title}
                  </h3>

                  <p className="text-sm text-[#2b2826]/65 font-light leading-relaxed line-clamp-2 mb-4" data-id="element-722">
                    {post.excerpt}
                  </p>

                  <p className="text-xs text-[#2b2826]/60" data-id="element-723">
                    By{' '}
                    <span className="text-[#2b2826] font-medium" data-id="element-724">
                      {post.author}
                    </span>
                  </p>
                </Link>)}
            </div>

            {totalPages > 1 && <div className="flex justify-center items-center gap-2 mt-16 sm:mt-20" data-id="element-725">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="!rounded-full !shadow-none hover:!shadow-none hover:!translate-y-0 !min-h-0 w-10 h-10 flex items-center justify-center border border-[#2b2826]/20 text-[#2b2826] disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#c9a875] hover:text-[#c9a875] transition-colors" data-id="element-726">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-727" />
                </button>

                {[...Array(totalPages)].map((_, i) => <button key={i} onClick={() => setCurrentPage(i + 1)} className={`!rounded-full !shadow-none hover:!shadow-none hover:!translate-y-0 !min-h-0 w-10 h-10 flex items-center justify-center text-sm font-medium transition-colors ${currentPage === i + 1 ? 'bg-[#2b2826] text-[#f2efeb]' : 'text-[#2b2826] hover:bg-[#2b2826]/5'}`} data-id="element-728">
                    {i + 1}
                  </button>)}

                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="!rounded-full !shadow-none hover:!shadow-none hover:!translate-y-0 !min-h-0 w-10 h-10 flex items-center justify-center border border-[#2b2826]/20 text-[#2b2826] disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#c9a875] hover:text-[#c9a875] transition-colors" data-id="element-729">
                  <ArrowRightIcon className="w-4 h-4" data-id="element-730" />
                </button>
              </div>}
          </>}
      </div>

      {/* CTA */}
      <div className="bg-[#2b2826] py-20 border-t border-[#403a35]" data-id="element-731">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-732">
          <p className="text-xs tracking-widest uppercase text-[#c9a875] font-medium mb-4" data-id="element-733">
            Ready to scale?
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#f2efeb] mb-5 leading-tight" data-id="element-734">
            Reading is good. <span className="italic" data-id="element-735">Funding</span> is better.
          </h2>
          <p className="text-[#f2efeb]/70 mb-8 max-w-xl mx-auto font-light" data-id="element-736">
            Apply for transactional funding in under five minutes. No credit
            checks, no upfront fees, no surprises.
          </p>
          <Link to="/#funding-form" className="inline-flex items-center gap-2 bg-[#c9a875] hover:bg-[#b08e5c] text-white font-medium py-3.5 px-7 rounded-full transition-colors text-sm" data-id="element-737">
            Apply for funding
            <ArrowRightIcon className="w-4 h-4" data-id="element-738" />
          </Link>
        </div>
      </div>

      <Footer data-id="element-739" />
    </div>;
};